import firebase from 'firebase/app';
import 'firebase/storage';

class AuthenticatedUrlCache {
  static cache = new Map();
  static cacheExpiry = 30 * 60 * 1000; // 30 minutes

  /**
   * Get cached authenticated URL or fetch new one
   */
  static async getAuthenticatedUrl(originalUrl) {
    const cacheKey = this.generateCacheKey(originalUrl);
    const cached = this.cache.get(cacheKey);

    // Check if cached URL is still valid
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.url;
    }

    // Fetch new authenticated URL
    const authenticatedUrl = await this.fetchAuthenticatedUrl(originalUrl);
    
    // Cache the result
    this.cache.set(cacheKey, {
      url: authenticatedUrl,
      timestamp: Date.now()
    });

    return authenticatedUrl;
  }

  /**
   * Generate cache key from URL
   */
  static generateCacheKey(url) {
    return `auth_url_${btoa(url).slice(0, 50)}`;
  }

  /**
   * Fetch authenticated URL from Firebase
   */
  static async fetchAuthenticatedUrl(url) {
    try {
      // Check if user is authenticated
      const currentUser = firebase.auth().currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Force token refresh to ensure it's valid
      const idToken = await currentUser.getIdToken(true);

      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+?)\?/);
      
      if (pathMatch) {
        const filePath = decodeURIComponent(pathMatch[1]);
        
        const storageRef = firebase.storage().ref(filePath);
        
        try {
          // Get download URL with authentication
          const downloadURL = await storageRef.getDownloadURL();
          return downloadURL;
        } catch (storageError) {
          throw storageError;
        }
      }
    } catch (error) {
      console.error('Failed to get authenticated URL:', error);
      throw error; // Re-throw to handle in component
    }
    return url;
  }

  /**
   * Clear expired cache entries
   */
  static clearExpired() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheExpiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  static getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.entries()).map(([key, value]) => ({
        key: key.slice(0, 20) + '...',
        age: Date.now() - value.timestamp,
        url: value.url.slice(0, 50) + '...'
      }))
    };
  }
}

export default AuthenticatedUrlCache; 